import { addSnackBarMessage, showOffline } from '@/core/store/globalStore'
import { SW_MESSAGE_TYPES } from '@/service-worker/shared/Constants'
import { environment } from '@/config/environment'

let isServiceWorkerSetup = false

export const setupServiceWorker = (): void => {
    if (isServiceWorkerSetup || !('serviceWorker' in navigator)) return
    isServiceWorkerSetup = true

    navigator.serviceWorker.ready.then((registration) => {
        registration.active?.postMessage({
            type: SW_MESSAGE_TYPES.INIT_CONFIG,
            backendUrl: environment.BACKEND_URL,
        })
        // Mutations may have been queued while the app was closed.
        if (navigator.onLine) triggerQueuedMutationSync()
    })

    navigator.serviceWorker.addEventListener('message', handleSWMessage)

    const channel = new BroadcastChannel('network-status')

    window.addEventListener('online', () => {
        channel.postMessage({ type: SW_MESSAGE_TYPES.NETWORK_STATUS, status: 'online' })
        triggerQueuedMutationSync()
    })
    window.addEventListener('offline', () =>
        channel.postMessage({ type: SW_MESSAGE_TYPES.NETWORK_STATUS, status: 'offline' })
    )
}

// The browser's Background Sync event is unreliable (and absent on iOS), so the
// replay is always requested explicitly as well; the service worker coalesces runs.
const triggerQueuedMutationSync = (): void => {
    navigator.serviceWorker.controller?.postMessage({
        type: SW_MESSAGE_TYPES.TRIGGER_FALLBACK_BG_SYNC,
    })
}

const handleSWMessage = ({ data }: MessageEvent): void => {
    const { type, url, method, status, ok, body } = data || {}

    switch (type) {
        case SW_MESSAGE_TYPES.OFFLINE_MUTATION_QUEUED:
            notify(`Queued offline request: ${method} ${url}`)
            break

        case SW_MESSAGE_TYPES.OFFLINE_MUTATION_RESPONSE:
            handleMutationResponse({ method, url, status, ok, body })
            break

        case SW_MESSAGE_TYPES.PRELOAD_COMPLETE:
            console.info('API preloading complete.')
            break

        case SW_MESSAGE_TYPES.NETWORK_STATUS:
            handleNetworkStatus(status)
            break
    }
}

// Only updates UI. The replay is triggered from this client's own `online`
// event, since this handler also runs for status broadcasts echoed back from
// the service worker on behalf of every other open client.
const handleNetworkStatus = (status: 'online' | 'offline'): void => {
    const isOnline = status === 'online'

    showOffline(!isOnline)
    notify(isOnline ? 'You are back online.' : 'You are offline. Changes will be synced when back online.')
}

const handleMutationResponse = ({
    method,
    url,
    status,
    ok,
    body,
}: {
    method: string
    url: string
    status: number
    ok: boolean
    body?: unknown
}) => {
    const summary =
        typeof body === 'string'
            ? body
            : (body as any)?.message || (body as any)?.error || (body as any)?.title || JSON.stringify(body)

    const msg = ok ? `Successfully Synced: ${method} ${url}` : `Sync failed: ${method} ${url} - ${status})`

    notify(msg)
}

const notify = (message: string): void => {
    addSnackBarMessage(message)
}
